import moment from 'moment';

export const MIT_SEGMENT_DATE_FORMAT = 'MMM DD, YYYY';
export const MIT_SAVE_DATE_FORMAT = 'MM/DD/YYYY';

export interface MitGoalDateBounds {
  start_date: unknown;
  target_date: unknown;
}

export interface MitDateSegmentEndRow {
  endDate?: string;
}

/** Goal dates may be unix seconds or a formatted string from the server. */
export function parseUtcGoalDate(date: unknown): moment.Moment {
  const isNumeric = typeof date === 'number'
    || (typeof date === 'string' && date !== '' && !Number.isNaN(Number(date)));
  return isNumeric
    ? moment.unix(Number(date)).utc().startOf('day')
    : moment.utc(date).startOf('day');
}

/** Parse MIT segment / picker input dates as UTC calendar days. */
export function parseUtcSegmentDate(dateStr: string): moment.Moment {
  return moment.utc(dateStr.trim(), MIT_SEGMENT_DATE_FORMAT, true).startOf('day');
}

/** Map a UTC calendar day to a local Date for react-day-picker (day-only, no TZ shift). */
export function utcMomentToPickerDate(utcMoment: moment.Moment): Date {
  const day = utcMoment.clone().utc();
  return new Date(day.year(), day.month(), day.date());
}

export function getMitIntervalMinUtc(
  goalDetails: MitGoalDateBounds,
  dateSegments: MitDateSegmentEndRow[],
  mitIndex: number,
): moment.Moment {
  const okrStartUtc = parseUtcGoalDate(goalDetails.start_date);
  if (mitIndex < 1) {
    return okrStartUtc;
  }

  const segmentMinUtc = parseUtcSegmentDate(dateSegments[mitIndex - 1].endDate ?? '')
    .add(1, 'day');
  return segmentMinUtc.isAfter(okrStartUtc) ? segmentMinUtc : okrStartUtc;
}

export function getMitIntervalMaxUtc(goalDetails: MitGoalDateBounds): moment.Moment {
  return parseUtcGoalDate(goalDetails.target_date);
}

export function isInvalidCustomMitStartDate(
  startDateStr: string,
  goalDetails: MitGoalDateBounds,
  dateSegments: MitDateSegmentEndRow[],
  mitIndex: number,
): boolean {
  const trimmed = startDateStr.trim();
  if (trimmed === '') {
    return true;
  }
  const startUtc = parseUtcSegmentDate(trimmed);
  if (!startUtc.isValid()) {
    return true;
  }
  const minUtc = getMitIntervalMinUtc(goalDetails, dateSegments, mitIndex);
  const maxUtc = getMitIntervalMaxUtc(goalDetails);
  return startUtc.isBefore(minUtc, 'day') || startUtc.isAfter(maxUtc, 'day');
}

export function isInvalidCustomMitEndDate(
  endDateStr: string,
  goalDetails: MitGoalDateBounds,
): boolean {
  const trimmed = endDateStr.trim();
  if (trimmed === '') {
    return true;
  }
  const endUtc = parseUtcSegmentDate(trimmed);
  if (!endUtc.isValid()) {
    return true;
  }
  const maxUtc = getMitIntervalMaxUtc(goalDetails);
  return endUtc.isAfter(maxUtc, 'day');
}

export function formatUtcSegmentForSave(dateStr: string): string {
  return parseUtcSegmentDate(dateStr.trim()).format(MIT_SAVE_DATE_FORMAT);
}

/** Fallback segment: OKR start through start + 1 month, capped at OKR end (UTC calendar days). */
export function createUtcDateSegmentsFallback(
  krStartDate: unknown,
  krTargetDate: unknown,
) {
  const startUtc = parseUtcGoalDate(krStartDate);
  const targetUtc = parseUtcGoalDate(krTargetDate);
  const oneMonthLaterUtc = startUtc.clone().add(1, 'month');
  const endUtc = oneMonthLaterUtc.isAfter(targetUtc) ? targetUtc : oneMonthLaterUtc;

  return {
    startDate: startUtc.format(MIT_SEGMENT_DATE_FORMAT),
    endDate: endUtc.format(MIT_SEGMENT_DATE_FORMAT),
    intervalName: '',
  };
}

export function buildMitDayPickerProps(
  goalDetails: MitGoalDateBounds,
  dateSegments: MitDateSegmentEndRow[],
  mitIndex: number,
) {
  const minPickerDate = utcMomentToPickerDate(
    getMitIntervalMinUtc(goalDetails, dateSegments, mitIndex),
  );
  const maxPickerDate = utcMomentToPickerDate(getMitIntervalMaxUtc(goalDetails));

  return {
    month: minPickerDate,
    fromMonth: minPickerDate,
    disabledDays: [
      { before: minPickerDate },
      { after: maxPickerDate },
    ],
  };
}
